import type { Step } from '@de/types/types';

/**
 * Step-level correlation payload shared by every in-flow event emitted
 * for a DSL-rendered step (§16.3 of the Dynamic Experience Eventing
 * Requirements; cf. Child SAD §9.4 and Eventing Architecture SAD §6.6).
 *
 * `StepCorrelation` is rebuilt on every step transition; the upstream
 * chain (`elementLoadedCorrelationId` → `viewCorrelationId` →
 * `formViewedCorrelationId`) is owned by `useDynamicExperienceEventing`
 * and survives across the standard ↔ DSL render boundary unchanged.
 */
export interface StepCorrelation {
  readonly stepId: string;
  readonly stepIndex: number;
  readonly stepType: string;
  readonly dslConfigId?: string;
  /**
   * Published config version. Present only when it is an integer of at
   * least 1, so an unsaved preview with no version omits the field.
   */
  readonly dslConfigVersion?: number;
  readonly pageCategory?: string;
}

export interface BuildStepCorrelationParams {
  readonly currentStep?: Step;
  readonly currentStepIndex?: number;
  readonly dslConfigId?: string;
  /**
   * Published config version from the step's inline `dslConfig`.
   * Absent, `0`, and non-integers are omitted.
   */
  readonly dslConfigVersion?: unknown;
  /**
   * Optional caller-supplied page category. When omitted, defaults to
   * `dexp-step` so every emitted in-flow event carries a stable
   * categorical anchor for downstream funnel analysis.
   */
  readonly pageCategory?: string;
}

/**
 * Integer config version of at least 1. Anything else is absent,
 * including `0` and a non-integer, so an unsaved preview still renders.
 */
export function publishedConfigVersion(version: unknown): number | undefined {
  if (
    typeof version !== 'number' ||
    !Number.isInteger(version) ||
    version < 1
  ) {
    return undefined;
  }
  return version;
}

/**
 * Default page category emitted on `StepCorrelation` when the caller
 * does not supply one. Stable string used by Cohesion funnel reports.
 */
export const DEFAULT_STEP_PAGE_CATEGORY = 'dexp-step';

/**
 * Build a `StepCorrelation` object for the current DSL step. Pure —
 * identical inputs produce structurally equal output.
 *
 * `stepId` falls back to an empty string rather than throwing so the
 * builder can be called during transient renders where `currentStep`
 * has not yet been initialised. Consumers that need a guaranteed-valid
 * id should gate on `stepId !== ''` before firing events.
 */
export const buildStepCorrelation = ({
  currentStep,
  currentStepIndex,
  dslConfigId,
  dslConfigVersion,
  pageCategory,
}: BuildStepCorrelationParams): StepCorrelation => {
  const version = publishedConfigVersion(dslConfigVersion);
  return {
    stepId: currentStep?.id ?? '',
    stepIndex: typeof currentStepIndex === 'number' ? currentStepIndex : 0,
    stepType: (currentStep?.type as string | undefined) ?? '',
    dslConfigId,
    ...(version !== undefined ? { dslConfigVersion: version } : {}),
    pageCategory: pageCategory ?? DEFAULT_STEP_PAGE_CATEGORY,
  };
};
